<script setup lang="ts">
import { PropType } from "vue";
import { IPlaceCard } from "@/package/global-types";
import { IconFirstFill, IconStar100Regular } from "@packages/icon-library";
import { UiBadge } from "@packages/component-library";
import { useI18n } from "vue-i18n";

defineProps({
  place: {
    type: Object as PropType<IPlaceCard>,
    required: true,
  },
});

const { t } = useI18n();
</script>

<template>
  <div class="place-card">
    <RouterLink :to="{ query: { place: place.id } }">
      <div
        class="place-card__image"
        :style="{ backgroundImage: `url(${place.image_src})` }"
      >
        <div class="place-card__labels">
          <UiBadge
            v-if="place?.badges.includes('top')"
            :label="t('place.badges.top')"
            size="s"
            type="soft"
            color="pink"
          >
            <IconFirstFill />
          </UiBadge>
        </div>
      </div>

      <div class="place-card__info">
        <p>
          <template v-if="place.badges.includes('online')">
            {{ t("place.info.online") }} <span>•</span>
          </template>
          {{ place.category_title }}
        </p>
        <span v-if="place.rating">
          <IconStar100Regular /> {{ place.rating.toFixed(1) }}
        </span>
      </div>

      <h4>{{ place.title }}</h4>
    </RouterLink>
  </div>
</template>

<style scoped lang="scss">
.place-card {
  flex-shrink: 0;

  > a {
    > h4 {
      margin: calc($base-space * 2) 0 0;
      @include body-bold-l();
      color: var(--color-semantic-content-normal-primary);
      @include max-stroke(2);
    }
  }

  &__image {
    aspect-ratio: 16/9;
    border-radius: 16px;
    @include centerBackgroundImageCover();
    position: relative;
  }

  &__labels {
    position: absolute;
    left: 16px;
    bottom: 16px;

    > .ui-badge_soft_pink {
      background: linear-gradient(
          0deg,
          var(
              --color-non-semantic-background-soft-pink,
              rgba(255, 112, 208, 0.1)
            )
            0%,
          var(
              --color-non-semantic-background-soft-pink,
              rgba(255, 112, 208, 0.1)
            )
            100%
        ),
        var(--color-semantic-background-normal-bg-primary);
    }
  }

  &__info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: calc($base-space * 4);
    margin: calc($base-space * 4) 0 0;

    > p {
      @include body-m();
      color: var(--color-semantic-content-normal-tertiary);
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: calc($base-space * 2);
      @include max-stroke(1);

      > span {
        color: var(--color-semantic-content-normal-contrast-secondary);
      }
    }

    > span {
      @include body-m();
      color: var(--color-semantic-content-normal-secondary);
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: calc($base-space * 1.5);

      > .icon {
        background-color: var(--color-semantic-content-normal-brand);
      }
    }
  }
}
</style>
